import { Consideration } from './Consideration';
import {
  ParsedSupplement,
  parseSupplementItem,
} from '../utils/supplementItem';
import { ABOUT_MARKER_MAP } from './about-marker-map';
import { normalizeSupplementName } from './holobiomeKits';

/**
 * One product in the cat 500xxx supplement catalog, as anything outside this
 * model sees it. The DynamoDB row keeps its description and link in `items`
 * positionally; nothing else should have to know that.
 */
export type CatalogSupplement = {
  tag: string;
  name: string;
  description: string;
  link?: string;
  /** LabSmarts report sections this product is for. Empty when unfiled. */
  sections: string[];
  brand?: string;
};

/**
 * A catalog row: no `tab` (so it is looked up by tag and never rendered as a
 * tab of its own), filed under Supplements, and carrying a product name.
 * Pattern Supplements bullets are a different thing entirely — they DO have a
 * tab, and they reference these rows by tag.
 */
function isSupplementCatalogRow(consideration: Consideration): boolean {
  return (
    !!consideration &&
    !consideration.tab &&
    consideration.section === 'Supplements' &&
    !!consideration.title
  );
}

export class FurtherConsiderations {
  considerations: Consideration[] = [];

  static createFromConsiderations(considerations: Consideration[]) {
    const furtherConsiderations = new FurtherConsiderations();

    if (considerations) {
      furtherConsiderations.considerations = considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      furtherConsiderations.considerations = [];
    }

    return furtherConsiderations;
  }

  constructor(furtherConsiderations?: FurtherConsiderations) {
    if (!furtherConsiderations) {
      return;
    }

    Object.keys(furtherConsiderations).forEach(key => {
      this[key] = furtherConsiderations[key];
    });

    if (furtherConsiderations.considerations) {
      this.considerations = furtherConsiderations.considerations.map(
        consideration => {
          return new Consideration(consideration);
        },
      );
    } else {
      this.considerations = [];
    }
  }

  findConsiderations(
    categoryId: number,
    section?: string,
    tab?: string,
    brands?: string[],
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      const isBrandMatch = !brands || brands.includes(consideration.brand);

      if (section && tab) {
        return (
          consideration.categoryId === categoryId &&
          consideration.section === section &&
          consideration.tab === tab &&
          isBrandMatch
        );
      }

      return consideration.categoryId === categoryId && isBrandMatch;
    });
  }

  findConsiderationsByCategoryAndSubCategory(
    categoryId: number,
    subCategoryId: number,
  ): Consideration[] {
    return (this.considerations || []).filter(consideration => {
      return (
        consideration.categoryId === categoryId &&
        consideration.subCategoryId === subCategoryId
      );
    });
  }

  // Resolve a marker's "About" description for a given section, keyed off the
  // STABLE categoryId via ABOUT_MARKER_MAP (generated by
  // scripts/buildAboutMarkerMap.js). Because nothing here matches on the sheet
  // `title`, reformatting a marker title in the sheet never breaks the lookup.
  //   1) the marker's own row in this section, else
  //   2) a safe cross-section fallback: if the marker maps to exactly ONE
  //      categoryId table-wide (e.g. a Glucose-borrowed marker described only in
  //      its home section), use it. Markers described in several sections
  //      (ferritin, HDL, cortisol) map to >1 categoryId, so they never fall back
  //      to a wrong-context write-up.
  findAboutConsideration(
    section: string,
    fieldId: string,
  ): Consideration | undefined {
    const sectionMap = ABOUT_MARKER_MAP[section];
    let categoryId = sectionMap ? sectionMap[fieldId] : undefined;

    if (categoryId === undefined) {
      const categoryIds: number[] = [];
      for (const sec of Object.keys(ABOUT_MARKER_MAP)) {
        const cid = ABOUT_MARKER_MAP[sec][fieldId];
        if (cid !== undefined && categoryIds.indexOf(cid) === -1) {
          categoryIds.push(cid);
        }
      }
      if (categoryIds.length === 1) categoryId = categoryIds[0];
    }

    if (categoryId === undefined) return undefined;
    return (this.considerations || []).find(
      c => c.tab === 'About' && c.categoryId === categoryId,
    );
  }

  // Resolve a Reference Library slug (e.g. "ada_diagnosis_classification_diabetes_2026")
  // to its Consideration row. Tags are unique across the table — the sheet's
  // Reference Library tab enforces it — so the first match wins.
  findByTag(tag: string): Consideration | undefined {
    if (!tag) return undefined;
    return (this.considerations || []).find(c => c.tag === tag);
  }

  // Resolve a Supplements bullet to its display parts. A bullet is either a
  // supplement-library tag (a cat 500xxx row — the de-duplicated form, items =
  // [description, link]) or a legacy inline "Name — Description — url" string.
  // findByTag is tried FIRST: supplement tags can contain spaces/hyphens (e.g.
  // "ga - adrenal", "cvo-r") that parseSupplementItem would mis-split on its
  // " - " separator, and a legacy string never matches a tag. Returns null for
  // unstructured lines (e.g. the "Use code …" promo) so callers render them as
  // plain text. Mirrors the Reference Library's findByTag resolution.
  resolveSupplement(item: string): ParsedSupplement | null {
    const tag = (item || '').trim();
    const row = this.findByTag(tag);
    if (row) {
      const [description = '', link = ''] = row.items || [];
      return { name: row.title, description, link: link || undefined };
    }
    return parseSupplementItem(item);
  }

  // Resolve a supplement's product URL by its display NAME (title), from the
  // cat 500xxx supplement catalog. Unlike resolveSupplement (keyed by tag on a
  // recommendation bullet), this looks a product up by name so the Holobiome
  // Roadmap panel — which lists every product in a kit, including grayed-out
  // ones no pattern recommended — can still link to the real Systemic Formulas
  // page. Catalog rows carry NO `tab` and store [description, link] in items;
  // matching the sheet's own URL avoids slug-guessing (e.g. "Terra µBiomic"
  // encodes as terra-%c2%b5biomic, not terra-ubiomic). Returns undefined when
  // the product isn't in the catalog yet, so callers can fall back.
  supplementLinkByName(name: string): string | undefined {
    const target = normalizeSupplementName(name);
    if (!target) return undefined;
    for (const c of this.considerations || []) {
      if (!isSupplementCatalogRow(c)) continue;
      if (normalizeSupplementName(c.title) !== target) continue;
      const link = (c.items || [])[1];
      if (link && /^https?:\/\//.test(link)) return link;
    }
    return undefined;
  }

  /**
   * The whole supplement catalog, as the "add a supplement" picker sees it.
   *
   * Same rows `supplementLinkByName` reads one of, which is why both go through
   * isSupplementCatalogRow: two predicates for "what counts as a catalog row"
   * would eventually disagree, and the symptom would be a product that can be
   * picked but never links.
   *
   * Deduped by product NAME rather than by tag. A product with a second,
   * context-specific tag (the sheet's Tag2 column, e.g. a description written
   * for the digestion patterns) has two catalog rows and is still one bottle;
   * the row carrying a description wins, so the picker never offers the same
   * product twice or shows it as blank when a description exists.
   */
  supplementCatalog(brands?: string[]): CatalogSupplement[] {
    const byName = new Map<string, CatalogSupplement>();
    for (const c of this.considerations || []) {
      if (!isSupplementCatalogRow(c)) continue;
      // AN EMPTY LIST MEANS "NO BRAND IS SWITCHED ON", NOT "NO FILTER".
      // findConsiderations already reads it that way, and the two disagreeing
      // is what left the "Add a supplement" picker offering all 195 Systemic
      // Formulas products on a profile where that brand had been unchecked.
      // `undefined` is still the unfiltered call, which is how a caller asks
      // what brand a product belongs to (see catalogIndex).
      if (brands && c.brand && brands.indexOf(c.brand) === -1) {
        continue;
      }
      const key = normalizeSupplementName(c.title);
      const [description = '', link = ''] = c.items || [];
      const candidate: CatalogSupplement = {
        tag: c.tag || '',
        name: c.title,
        description,
        link: link && /^https?:\/\//.test(link) ? link : undefined,
        // Which report sections this product is for, from the sheet's own
        // column. Absent on rows added before that column existed, which the
        // picker groups as unfiled rather than hiding.
        sections: Array.isArray(c.sections) ? c.sections.filter(Boolean) : [],
        brand: c.brand,
      };
      const existing = byName.get(key);
      if (!existing) {
        byName.set(key, candidate);
        continue;
      }
      // Keep the richer row: a description beats none, and sections beat none.
      if (!existing.description && candidate.description) {
        byName.set(key, { ...candidate, sections: candidate.sections.length ? candidate.sections : existing.sections });
      } else if (!existing.sections.length && candidate.sections.length) {
        byName.set(key, { ...existing, sections: candidate.sections });
      }
    }
    return Array.from(byName.values());
  }
}